import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CourseForm } from '../components/CourseForm';
import { useCourse, useCreateCourse, useUpdateCourse } from '../queries';
import type { CourseInput } from '../types';

export function CourseFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const courseQuery = useCourse(isEdit ? publicId : -1);
  const create = useCreateCourse();
  const update = useUpdateCourse(publicId);

  async function handleSubmit(values: CourseInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/lms/courses/${saved.public_id}`);
  }

  if (isEdit && courseQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const course = courseQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/lms/courses/${publicId}` : '/lms/courses')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${course?.title ?? 'course'}` : 'New course'}
        </h1>
      </div>

      <CourseForm
        submitLabel={isEdit ? 'Save changes' : 'Create course'}
        defaultValues={
          course
            ? {
                title: course.title,
                description: course.description,
                category: course.category,
                is_published: course.is_published,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/lms/courses/${publicId}` : '/lms/courses')}
      />
    </div>
  );
}
